import React, { useContext, useEffect, useState } from 'react';
import { shallowEqual, useSelector, useDispatch } from 'react-redux';
import { useForm, FormProvider } from 'react-hook-form';
import {
  Box,
  Heading,
  Metadata,
  PrimaryButton,
  SecondaryButton,
  Spinner,
  Text,
  Modal,
  ModalContainer,
  LinkButton,
} from 'components';
import { I18nContext } from 'components/I18n';
import DeleteIcon from 'components/Icons/DeleteIcon';
import { ModalHeading } from 'components/Modal/ModalStyles';
import { FormField, TextField } from 'components/Forms';
import validate from 'utils/validate';
import { RouterLocation } from 'router';
import { useUpdateProfileMutation } from './UpdateProfile.generated';
import { useCreateProfileMutation } from './CreateProfile.generated';
import { useDeleteProfileMutation } from './DeleteProfile.generated';
import { ProfilesDocument } from './GetProfiles.generated';
import { useAvatarsQuery } from './GetAvatars.generated';
import { ProfileType, isDefaultProfile, markProfileActivityAsOutdated, PROFILE_AVATAR_SIZE } from './utils';
import * as routerActions from 'router/actions';
import { useProfileContext } from './ProfileContext';
import AvatarImage from './AvatarImage';
import { ProfilesLayout } from './Styles';
import { addMessage } from 'actions/messages';

type ProfileEditLocation = {
  params: {
    profileId?: string,
  },
};

type ProfileEditViewProps = {
  location: RouterLocation & ProfileEditLocation,
};

function ProfileEditView({ location }: ProfileEditViewProps) {
  const {
    profiles,
    profilesLoading,
    profilesError,
    refreshProfiles,
    currentProfile,
    setEditProfile,
    editProfile,
    openReturnLocation,
  } = useProfileContext();

  const i18n = useContext(I18nContext);
  const profileId = location?.params?.profileId;
  const isNewProfile = !profileId;
  const dispatch = useDispatch();
  const FormFieldAny: any = FormField;
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const { brandName, userId } = useSelector((state: any) => ({
    brandName: state.settings.brand.name,
    userId: state.auth.userId,
  }), shallowEqual);
  const methods = useForm({
    mode: 'onChange',
    defaultValues: {
      name: editProfile?.name || '',
    },
  });
  // eslint-disable-next-line @typescript-eslint/unbound-method
  const { handleSubmit, getValues, reset } = methods;

  const [updateProfile, { loading: updating }] = useUpdateProfileMutation();
  const [createProfile, { loading: creating }] = useCreateProfileMutation();
  const [deleteProfile, { loading: deleting }] = useDeleteProfileMutation();

  const { data: avatarsData } = useAvatarsQuery({
    skip: !isNewProfile,
  });

  useEffect(() => {
    if (profilesError) {
      openReturnLocation();
    }
  }, [profilesError, openReturnLocation]);

  useEffect(() => {
    const profile = isNewProfile ? undefined : profiles?.find(p => p.id === profileId);

    if (profile?.id !== editProfile?.id) {
      reset({
        name: profile?.name || '',
      });
      setEditProfile(profile);
    }
  }, [isNewProfile, profiles, profileId, editProfile?.id, setEditProfile]);

  // Preselect first avatar for new profiles
  useEffect(() => {
    if (isNewProfile
      && !editProfile?.imageUrl
      && avatarsData?.viewer?.profileAvatars
      && avatarsData.viewer.profileAvatars.length > 0
    ) {
      const firstAvatar = avatarsData.viewer.profileAvatars[0];

      if (firstAvatar?.imageUrl) {
        setEditProfile({
          ...(editProfile || {}),
          imageUrl: firstAvatar.imageUrl,
        } as ProfileType);
      }
    }
  }, [isNewProfile, avatarsData, editProfile, setEditProfile]);

  const determineReturnRoute = () => {
    if (location?.name === 'profile-new') {
      return { name: 'profiles' };
    }
    return { name: 'profiles-edit' };
  };

  const navigateBack = () => {
    const targetLocation = determineReturnRoute();
    dispatch(routerActions.replace(targetLocation));
  };

  const onCancel = () => {
    if (updating || creating) return;
    navigateBack();
    setEditProfile(undefined);
  };

  const onSubmit = async (values: { name?: string; }) => {
    try {
      if (editProfile?.id) {
        await updateProfile({
          variables: {
            id: editProfile.id,
            name: values.name ?? null,
            imageUrl: editProfile.imageUrl ?? null,
          },
          refetchQueries: [{ query: ProfilesDocument }],
        });
      } else {
        await createProfile({
          variables: {
            name: values.name ?? null,
            imageUrl: editProfile?.imageUrl ?? null,
          },
          refetchQueries: [{ query: ProfilesDocument }],
        });
      }
      navigateBack();
      setEditProfile(undefined);
    } catch (e) {
      dispatch(addMessage({ contentId: 'failedMessage' }));
    }
  };

  const isDefault = isDefaultProfile(editProfile?.id, userId);

  const openSelectAvatar = () => {
    const currentValues = getValues();
    const profileIdParam = editProfile?.id ?? 'new';
    const currentRouteName = location?.name;
    const returnTo = currentRouteName === 'profile-new' || currentRouteName === 'profile-new-edit'
      ? currentRouteName
      : undefined;

    setEditProfile({
      ...(editProfile || {}),
      name: currentValues.name,
    } as ProfileType);

    dispatch(routerActions.push({
      name: 'profile-avatar',
      params: { profileId: profileIdParam },
      ...(returnTo ? { query: { returnTo } } : {}),
    }));
  };

  const handleDeleteProfile = async () => {
    if (!editProfile?.id) return;
    try {
      await deleteProfile({
        variables: { id: editProfile.id },
        refetchQueries: [{ query: ProfilesDocument }],
      });

      if (currentProfile?.id === editProfile.id) {
        markProfileActivityAsOutdated();
      }

      navigateBack();
      // redirect is slower than updating context
      setTimeout(() => setEditProfile(undefined), 100);
    } catch (e) {
      dispatch(addMessage({ contentId: 'failedMessage' }));
    }
  };

  const onCancelDelete = () => {
    setShowDeleteConfirmation(false);
  };

  if (profilesLoading) {
    return (
      <ProfilesLayout>
        <Box column alignItems="center" justifyContent="center" p="xlarge">
          <Spinner size={3} />
        </Box>
      </ProfilesLayout>
    );
  }

  if (profilesError) {
    return <ProfilesLayout />;
  }

  if (!isNewProfile && !editProfile) {
    if (profiles?.find(p => p.id === profileId)) {
      return <ProfilesLayout />;
    }
    return (
      <ProfilesLayout>
        <Box column alignItems="center" justifyContent="center" p="xlarge" gap="medium">
          <Heading as="h1" fontSize="2rem">
            <Text id="profiles.editProfile" />
          </Heading>
          <Text id="profiles.profileNotFound" color="error" />
          <PrimaryButton onClick={refreshProfiles}>
            <Text id="retry" />
          </PrimaryButton>
        </Box>
      </ProfilesLayout>
    );
  }

  const titleId = editProfile?.id ? 'profiles.editProfile' : 'profiles.addProfile';

  return (
    <ProfilesLayout>
      <Metadata title={titleId} />

      <Box my="large" alignItems="center">
        <Heading id={titleId} />
      </Box>

      {!editProfile?.id && (
        <Box maxWidth="32rem" m="0 auto" xs-mb="large" textAlign="center">
          <Text id="profiles.addProfileDescription" values={{ brandName }} align="center" />
        </Box>
      )}

      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)} style={{ width: '100%' }}>
          <Box column alignItems="center" my="xxlarge" xs-my="fine" gap="giant" fullWidth>
            <Box
              gap="xlarge"
              xs-gap="giant"
              alignItems="center"
              justifyContent="center"
              row
              wrap
              fullWidth
            >
              <Box width="100%" maxWidth="22.5rem" xs-maxWidth="100%" xs-order="2" height="6rem">
                <FormFieldAny
                  as={TextField}
                  name="name"
                  type="text"
                  placeholder={i18n.formatText('name')}
                  validations={{
                    required: validate.required(),
                  }}
                />
              </Box>

              <Box xs-order="1">
                <AvatarImage
                  src={editProfile?.imageUrl}
                  size="12rem"
                  hover
                  editIcon
                  alt={editProfile?.name || undefined}
                  onClick={openSelectAvatar}
                />
              </Box>
            </Box>

            <Box
              column
              alignItems="center"
              xs-position="fixed"
              xs-bottom="0"
              xs-left="0"
              xs-right="0"
              xs-fullWidth
              xs-bg="page"
              xs-p="large"
            >
              <Box
                row
                gap="medium"
                justifyContent="center"
                wrap
                xs-columnReverse
                xs-fullWidth
                xs-gap="small"
                xs-alignItems="stretch"
              >
                <SecondaryButton
                  variant="white"
                  type="button"
                  onClick={onCancel}
                  disabled={updating || creating || deleting}
                >
                  <Text id="cancel" />
                </SecondaryButton>

                <PrimaryButton
                  variant="brand"
                  type="submit"
                  disabled={updating || creating || deleting}
                >
                  <Text id="save" />
                </PrimaryButton>
              </Box>

              {editProfile?.id && (
                <Box mt="xlarge" xs-mt="medium">
                  {isDefault ? (
                    <Text id="profiles.defaultProfileCantBeDeleted" align="center" />
                  ) : (
                  <LinkButton
                    onClick={(e: React.MouseEvent) => {
                      if (!deleting) {
                        setShowDeleteConfirmation(true);
                        e.preventDefault();
                      }
                    }}
                    noPadding
                    inline
                    >
                    <Box row alignItems="center" gap="small">
                      <DeleteIcon width="0.625rem" />
                      <Text id="profiles.deleteProfile" />
                    </Box>
                  </LinkButton>
                  )}
                </Box>
              )}
            </Box>
          </Box>
        </form>
      </FormProvider>

      {showDeleteConfirmation && editProfile && (
        <Modal
          onDismiss={onCancelDelete}
          aria-labelledby="delete-profile-title"
          aria-describedby="delete-profile-name delete-profile-desc"
          role="alertdialog"
        >
          <ModalContainer
            onClickClose={onCancelDelete}
            withWidth="medium"
            withShadow
          >
            <Box column>
              <Box mb="medium" mt="large">
                <ModalHeading id="profiles.deleteProfileTitle" domId="delete-profile-title" />
              </Box>

              <Box column alignItems="center" mt="large">
                <Box mb="medium">
                  <AvatarImage
                    src={editProfile.imageUrl}
                    size={PROFILE_AVATAR_SIZE}
                    alt={editProfile.name || undefined}
                  />
                </Box>
                <Box>
                  <Text bold fontSize="medium" domId="delete-profile-name">{editProfile.name}</Text>
                </Box>
              </Box>

              <Box my="xlarge" textAlign="center">
                <Text id="profiles.deleteProfileWarning" domId="delete-profile-desc" align="center" />
              </Box>

              <Box
                alignItems="center"
                spaceBetween
                wrap
                columnGap="medium"
                fullWidth
              >
                <Box mt="medium" xs-mx="auto">
                  <SecondaryButton
                    variant="white"
                    minWidth="15rem"
                    onClick={onCancelDelete}
                    disabled={deleting}
                  >
                    <Text id="cancel" />
                  </SecondaryButton>
                </Box>

                <Box mt="medium" xs-mx="auto">
                  <PrimaryButton
                    variant="brand"
                    minWidth="15rem"
                    onClick={handleDeleteProfile}
                    showSpinner={deleting}
                  >
                    <Box row alignItems="center" gap="small">
                      <DeleteIcon width="0.625rem" />
                      <Text id="profiles.deleteProfileConfirm" />
                    </Box>
                  </PrimaryButton>
                </Box>
              </Box>
            </Box>
          </ModalContainer>
        </Modal>
      )}
    </ProfilesLayout>
  );
}

export default ProfileEditView;

